import React from 'react';
import autoBind from 'react-autobind';
import { If, Then, Else } from 'react-if'

import Config from "../../Config.jsx"
import Localization from "../../Localization.jsx"
import {
  Indicator,
  Button,
  Tooltip,
  ActionIcon,
  SimpleGrid,
  Select,
  Card,
  Text,
  Badge,
  Group,
  Switch,
} from '@mantine/core';
import {
  IconClock,
  IconRefresh,
  IconEdit,
  IconMapPin,
  IconInfoCircle,
  IconReportAnalytics,
  IconFileTypePdf,
  IconTrash,
  IconUserCog,
} from '@tabler/icons-react';
import { downloadManual } from '../../tools/manualDownload.js';
import ChangeOwnerDialog from './ChangeOwnerDialog.jsx';
import { SortOrderToggle } from '../SortOrderToggle.jsx';
import { ListLoadingBlock } from '../ListLoadingPlaceholder.jsx';
import BackgroundImage from '../../images/background/test.png';

// Enough placeholder cards to fill the first couple of grid rows at the widest
// breakpoint, so the loading state occupies roughly the space the real cards will.
const LOADING_CARD_COUNT = 8;
// Media band (240) + the action row underneath it.
const LOADING_CARD_HEIGHT = 300;

const SUBJECT_CHIP_COLOR = '#bd6f0f';
const chipStyle = {
  fontSize: 11,
  fontWeight: 600,
  height: 20,
  paddingInline: 8,
  textTransform: 'none',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Match the drawer width from LocaleViewerBar.jsx
const drawerWidthExpanded = 220;

const styles = {
  container: {
    marginLeft: `${drawerWidthExpanded}px`,
    transition: 'margin-left 0.2s',
    width: `calc(100% - ${drawerWidthExpanded}px)`,
  },
  media: {
    height: 240,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 16px',
  },
  title: {
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

// QA-state colours (literal hex replacements for @mui/material/colors)
const blue500 = '#2196f3';
const green500 = '#4caf50';
const amber500 = '#ffc107';
const red500 = '#f44336';

class LocaleListView extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    this.state = {
      showSubjects: true,
      // The locale row the Change-owner dialog is open for; null = closed.
      changeOwnerLocale: null,
    };
    autoBind(this);
  }

  /**
   * Change owner — SCHOOL ADMIN, OWN SCHOOL ONLY. The icon renders only when
   * `canChangeOwner` below is true, and the server repeats both tests on
   * PATCH /api/epack/{id}/owner, so this is a shortcut, not the gate.
   */
  changeOwnerClicked(row) {
    this.setState({ changeOwnerLocale: row });
  }

  changeOwnerClosed() {
    this.setState({ changeOwnerLocale: null });
  }

  changeOwnerChanged() {
    // The author line on the card and the 「我的 LOCALE」 membership both come
    // from the list rows, so a refresh is the only honest way to show the change.
    if (this.props.parent && this.props.parent.refreshLocaleList) {
      this.props.parent.refreshLocaleList();
    }
  }

  overviewLocaleClicked(index) {
    // console.log('on overview clicked');
    this.props.parent.overviewLocale(index);
  }

  editLocaleClicked(index) {
    console.log("editLocaleClicked");
    this.props.parent.editLocaleByIndex(index);
  }

  exploreLocaleClicked(index) {
    let locale = this.props.parent.state.localeList[index];
    let position = {
      lat: parseFloat(locale.mid_lat),
      lng: parseFloat(locale.mid_lon)
    };
    this.props.parent.state.localeListInMap = [];
    this.props.parent.state.localeListInMap.push(locale);
    this.props.parent.changeView(1);
    this.props.parent.setLocaleCenter(position);
  }

  retrieveAnswerClicked(index) {
    this.props.parent.retrieveAnswerByIndex(index);
  }

  deleteLocaleClicked(id) {
    this.props.parent.deleteLocale(id);
  }

  /**
   * The 教師參考手冊 PDF an admin attached to this template, saved under the
   * LOCALE's own name rather than the blob's generated one (see
   * tools/manualDownload.js for why that needs a fetch and not an <a download>).
   *
   * Only the Extra LOCALE tab ever gets here: `manual_path` is shipped by that
   * one route (epack_service.get_list_epack_extra_locale_selected), so the icon
   * this hangs off simply does not render on the other three.
   */
  async downloadManualClicked(row) {
    const how = await downloadManual({
      path: row['manual_path'],
      displayName: row['name'] || row['manual_display_name'],
      suffix: Localization.strings.EXTRA_LOCALE_MANUAL_FILE_SUFFIX || '教師參考手冊',
    });
    if (how === 'none') {
      this.props.root.addNotification(
        Localization.strings.EXTRA_LOCALE_MANUAL_DOWNLOAD_FAILED
          || '無法下載教師參考手冊', false);
    }
  }

  renderControls() {
    const { parent } = this.props;
    const { activeFilter, orderBy, orderMode } = parent.state;

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: "10px", alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            variant={activeFilter === 'my_locale' ? 'light' : 'filled'}
            color="brand"
            onClick={() => parent.setFilter('my_locale')}
          >
            {Localization.strings.MY_LOCALE}
          </Button>
          <Button
            variant={activeFilter === 'school_locale' ? 'light' : 'filled'}
            color="brand"
            onClick={() => parent.setFilter('school_locale')}
          >
            {Localization.strings.LOCALE_OF_MY_SCHOOL_1}
          </Button>
          <Button
            variant={activeFilter === 'assessment' ? 'light' : 'filled'}
            color="brand"
            onClick={() => parent.setFilter('assessment')}
            rightSection={
              <Tooltip
                label={Localization.strings.LOCALE_ASSESSMENT_HELP}
                position="bottom"
                withArrow
                multiline
                w={280}
              >
                <span
                  style={{ display: 'inline-flex' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <IconInfoCircle size={16} />
                </span>
              </Tooltip>
            }
          >
            {Localization.strings.LOCALE_ASSESSMENT_1}
          </Button>
          {/* Extra Locale (預製教材範本): the templates this school selected.
              Hidden entirely when nothing was selected — most schools never
              will have. View-only; see editLocaleEnabled below. */}
          {parent.state.extraLocaleCount > 0 && (
            <Button
              variant={activeFilter === 'extra_locale' ? 'light' : 'filled'}
              color="brand"
              onClick={() => parent.setFilter('extra_locale')}
              rightSection={
                <Tooltip
                  label={Localization.strings.EXTRA_LOCALE_TAB_HELP}
                  position="bottom"
                  withArrow
                  multiline
                  w={280}
                >
                  <span
                    style={{ display: 'inline-flex' }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <IconInfoCircle size={16} />
                  </span>
                </Tooltip>
              }
            >
              {Localization.strings.EXTRA_LOCALE_TAB}
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Switch
            label={Localization.strings.SHOW_CARD_SUBJECTS}
            labelPosition="left"
            checked={this.state.showSubjects}
            onChange={(event) => this.setState({ showSubjects: event.currentTarget.checked })}
            color="brand"
            size="sm"
            style={{
              border: '1px solid #eee',
              borderRadius: 999,
              padding: '6px 10px',
            }}
          />
          <Select
            value={orderBy}
            onChange={(v) => parent.setOrderBy(v)}
            allowDeselect={false}
            data={[
              { value: 'date_created', label: Localization.strings.ORDER_BY_TIME_CREATED },
              { value: 'date_update', label: Localization.strings.ORDER_BY_TIME_MODIFIED },
            ]}
            w="max-content"
            styles={{
              input: { width: 'max-content', minWidth: 'max-content' },
              wrapper: { width: 'max-content' },
            }}
            comboboxProps={{ width: 'target' }}
          />
          <SortOrderToggle
            value={orderMode}
            onChange={() => parent.toggleOrderMode()}
            variant="subtle"
            color="gray"
            iconSize={20}
          />
          <Tooltip
            label={Localization.strings.REFRESH}
            position="bottom"
          >
            <ActionIcon variant="subtle" color="gray" onClick={() => parent.refreshLocaleList()}>
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>
    );
  }

  // "Not known yet": a list request is in flight and we have nothing to show
  // from a previous one. Laid out on the same grid as the real cards so the
  // page does not jump when they arrive.
  renderLoading() {
    const blocks = [];
    for (let i = 0; i < LOADING_CARD_COUNT; i++) {
      blocks.push(
        <div key={i} style={{ padding: 8 }}>
          <ListLoadingBlock height={LOADING_CARD_HEIGHT} />
        </div>
      );
    }

    return (
      <SimpleGrid
        cols={{ base: 1, sm: 1, md: 2, lg: 3, xl: 4 }}
        style={{ padding: '8px 5px 0 5px' }}
        role="status"
        aria-live="polite"
        aria-label={Localization.strings.LOADING_DATA}
      >
        {blocks}
      </SimpleGrid>
    );
  }

  render() {
    // Safety check: ensure parent props are available
    if (!this.props.parent || !this.props.parent.state) {
      console.log('📍 LocaleListView: Parent props not ready, showing loading...');
      return this.renderLoading();
    }

    let isAdminUser = this.props.root.isAdminUser();
    // Three states, not two: while the request is in flight and no earlier
    // result is on screen, the list is unknown — showing the "no result"
    // artwork there answers a question the server has not answered yet.
    const localeList = this.props.parent.state.localeList;
    const isLoading = this.props.parent.state.localeListLoading && localeList.length == 0;

    if (isLoading) {
      return (
        <div>
          {this.renderControls()}
          {this.renderLoading()}
        </div>
      );
    }

    return (
      <div>
        {this.renderControls()}
        <If condition={localeList.length == 0}>
          <Then>
            <div style={{display:'flex',alignItems:'center',justifyContent:'center', minHeight: 'calc(100vh - 140px)', position: 'relative'}}>
              <img style={{position: 'absolute',top: '40%',left: '50%',transform: 'translate(-50%,-50%)',height:'50%',width:'auto', maxHeight: '300px'}} src={BackgroundImage} />
              <Card withBorder shadow="sm" style={{position: 'absolute',top: '75%',left: '50%',transform: 'translate(-50%,-50%)', zIndex: 10}}>
                <Card.Section p="md">
                  {Localization.strings.LOCALE_SEARCH_RESULT_NONE}
                </Card.Section>
              </Card>
            </div>
          </Then>
          <Else>
            <SimpleGrid cols={{ base: 1, sm: 1, md: 2, lg: 3, xl: 4 }} style={{padding: '8px 5px 0 5px'}}>
              {this.props.parent.state.localeList.map( (row, i) => {
                // The read-only tab flag sits OUTSIDE the ownership disjunction on
                // purpose: `isAdminUser ||` would otherwise hand a CLST admin full
                // edit/delete over the shared master templates from this tab.
                let editLocaleEnabled = !this.props.parent.isReadOnlyFilter()
                  && (isAdminUser || (this.props.root.getSameSchoolEdit() && row['school_id'] == this.props.root.getMySchoolId()) || this.props.root.getMyUserId() == row['user_id']);
                let showSubmissionBadge = row['submission_new_count'] > 0 && editLocaleEnabled;
                // A school with no subscription in force keeps everything on this
                // card except the two that CHANGE content — Overview, Locate and
                // Retrieve answers stay live, and so does Delete: the rule is
                // "cannot edit", and a school that has lapsed still owns its data
                // and may still need the storage back.
                const canEditContent = editLocaleEnabled && this.props.root.canEditExistingLocale();
                // Re-authoring is a school-admin power over the school's OWN
                // locales — never on the read-only template tab, never on a
                // foreign-school row the Assessment tab lists. Independent of
                // the subscription gate: it changes no content.
                const canChangeOwner = !this.props.parent.isReadOnlyFilter()
                  && typeof this.props.root.isSchoolAdmin === 'function'
                  && this.props.root.isSchoolAdmin()
                  && row['school_id'] == this.props.root.getMySchoolId();
                let qaStateColor = blue500;
                let qaState = row['qa_state'];
                if (qaState == 1) {
                  qaStateColor = green500;
                }
                else if (qaState == 2) {
                  qaStateColor = amber500;
                }
                else if (qaState == 3) {
                  qaStateColor = red500;
                }

                return (
                  <div key={i} style={{padding: 8}}>
                    <Card
                      withBorder
                      shadow="sm"
                      padding={0}
                      role="button"
                      tabIndex={editLocaleEnabled ? 0 : -1}
                      aria-disabled={!editLocaleEnabled}
                      aria-label={`${Localization.strings.EDIT_HOTSPOTS}: ${row['name']}`}
                      style={{ cursor: editLocaleEnabled ? 'pointer' : 'default' }}
                      onClick={(event) => {
                        // `editLocaleEnabled`, NOT `canEditContent`: the card is
                        // where a lapsed school gets TOLD why the Edit icon is
                        // greyed out. editLocale() refuses and explains, which a
                        // dead card could not do.
                        if (!editLocaleEnabled || (event.target.closest && event.target.closest('button'))) {
                          return;
                        }
                        this.editLocaleClicked(i);
                      }}
                      onKeyDown={(event) => {
                        if (!editLocaleEnabled || (event.target.closest && event.target.closest('button'))) {
                          return;
                        }
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          this.editLocaleClicked(i);
                        }
                      }}
                    >
                      <Card.Section
                        component="div"
                      >
                        <div
                          style={{
                            ...styles.media,
                            position: 'relative',
                            // tn_path is NULL when the locale has no thumbnail file
                            // (LEFT JOIN miss); interpolating that requested
                            // "<UPLOAD_PATH>null" and drew a broken image.
                            backgroundImage: row['tn_path']
                              ? `url(${Config.UPLOAD_PATH() + row['tn_path']})`
                              : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        >
                          {(() => {
                            const kla = this.props.root.refs.userDataProfile
                              ? this.props.root.refs.userDataProfile.getKLAByID(row['kla'])
                              : null;
                            const klaLabel = kla
                              ? (Localization.strings.getLanguage() === "en" ? kla.name_eng : kla.name)
                              : (this.props.root.refs.userDataProfile ? 'Unknown KLA' : 'Loading...');
                            let subjectLabel = Localization.strings.UNSPECIFIED_SUBJECT;
                            if (row['subject'] != 0 && this.props.root.refs.userDataProfile) {
                              const subject = this.props.root.refs.userDataProfile.getSubjectByID(row['subject']);
                              subjectLabel = subject
                                ? (Localization.strings.getLanguage() === "en" ? subject.name_eng : subject.name)
                                : 'Unknown Subject';
                            } else if (row['subject'] != 0) {
                              subjectLabel = 'Loading...';
                            }
                            return (
                              <Group
                                gap={6}
                                wrap="wrap"
                                align="center"
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  right: 8,
                                  zIndex: 1,
                                }}
                              >
                                {this.state.showSubjects && (
                                  <Tooltip
                                    label={klaLabel}
                                    position="top"
                                  >
                                    <span style={{ display: 'inline-flex', maxWidth: '100%' }}>
                                      <Badge radius="xl" style={{ ...chipStyle, backgroundColor: `${SUBJECT_CHIP_COLOR}CC`, color: '#fff' }}>
                                        {subjectLabel}
                                      </Badge>
                                    </span>
                                  </Tooltip>
                                )}
                                <Tooltip
                                  position="top"
                                  label={
                                    <div>
                                      <div>{Localization.strings.UPDATED_DATE}: {row['date_update']}</div>
                                      <div>{Localization.strings.CREATED_DATE}: {row['date_created']}</div>
                                    </div>
                                  }
                                >
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      cursor: 'help',
                                      backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                      borderRadius: 999,
                                      padding: 3,
                                    }}
                                  >
                                    <IconClock size={12} color="#fff" />
                                  </span>
                                </Tooltip>
                              </Group>
                            );
                          })()}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              width: '100%',
                              padding: '10px 16px',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            }}
                          >
                            <Text fw={600} size="lg" c="white" style={styles.title}>
                              {row['name']}
                            </Text>
                            <Text size="xs" c="white" style={styles.title}>
                              {Localization.strings.AUTHOR}:
                              {isAdminUser ? (" <" + row['id'] + "> ") : " "}
                              {row['display_name'] || row['username']}
                            </Text>
                          </div>
                        </div>
                      </Card.Section>
                      <If condition={this.props.parent.state.isViewPublic}>
                        <Then>
                          <div style={{ padding: '10px 16px 0' }}>
                            <Text size="xs">
                              {Localization.strings.getLanguage() === "en" && row['school_name_eng'] !== "" ? row['school_name_eng'] : row['school_name']}
                            </Text>
                          </div>
                        </Then>
                      </If>
                      <div style={styles.actions}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          padding: '4px 8px',
                          backgroundColor: '#fff',
                          border: '1px solid #eee',
                          borderRadius: 999,
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.06)',
                          backdropFilter: 'blur(16px)',
                          WebkitBackdropFilter: 'blur(16px)',
                        }}
                      >
                        <Tooltip
                          label={editLocaleEnabled && !canEditContent
                            ? Localization.strings.LOCALE_EDIT_BLOCKED_TOOLTIP
                            : Localization.strings.EDIT_HOTSPOTS}
                          position="bottom"
                        >
                          {/* The span is what makes the tooltip reachable at all
                              here: a disabled Mantine ActionIcon takes no pointer
                              events, so the label explaining WHY it is disabled
                              would never appear on the one card state that needs
                              it. inline-flex keeps the icon row's layout. */}
                          <span style={{ display: 'inline-flex' }}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={()=>this.editLocaleClicked(i)}
                              disabled={!canEditContent}
                              radius="xl"
                            >
                              <IconEdit size={20} />
                            </ActionIcon>
                          </span>
                        </Tooltip>
                        <Tooltip
                          label={Localization.strings.OVERVIEW}
                          position="bottom"
                        >
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={()=>this.overviewLocaleClicked(i)}
                            radius="xl"
                          >
                            <IconInfoCircle size={20} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          label={Localization.strings.LOCATE_ON_MAP}
                          position="bottom"
                        >
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={()=>this.exploreLocaleClicked(i)}
                            radius="xl"
                          >
                            <IconMapPin size={20} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          label={Localization.strings.RETRIEVE_ANSWERS}
                          position="bottom"
                        >
                          <Indicator
                            inline
                            color="brand"
                            label={row['submission_new_count'] > 99 ? '99+' : row['submission_new_count']}
                            size={16}
                            offset={6}
                            position="top-end"
                            disabled={!showSubmissionBadge}
                            style={{ margin: '0 2px' }}
                          >
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={()=>this.retrieveAnswerClicked(i)}
                              // Deliberately ENABLED on the read-only tab too:
                              // reviewing your own students' answers is a read of
                              // YOUR school's submissions (they carry their own
                              // school_id), not of the template's owner, so it is
                              // both safe and the point of selecting a template.
                              // The one write behind this button is suppressed for
                              // this tab in LocaleViewerPage.retrieveAnswer.
                              radius="xl"
                            >
                              <IconReportAnalytics size={20} />
                            </ActionIcon>
                          </Indicator>
                        </Tooltip>
                        {/* 教師參考手冊 (PDF). Rendered off `manual_path` — the
                            JOINED file row — and NOT off the tab name: only the
                            Extra LOCALE route ships that column, and a template
                            whose manual is not uploaded yet (or whose file row
                            was deleted) sends it empty, so no icon appears
                            rather than one whose download 404s. */}
                        {row['manual_path'] ? (
                          <Tooltip
                            label={Localization.strings.EXTRA_LOCALE_MANUAL_DOWNLOAD}
                            position="bottom"
                          >
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={()=>this.downloadManualClicked(row)}
                              radius="xl"
                            >
                              <IconFileTypePdf size={20} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                        <Tooltip
                          label={Localization.strings.DELETE}
                          position="bottom"
                        >
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={()=>this.deleteLocaleClicked(row['id'])}
                            disabled={!editLocaleEnabled}
                            radius="xl"
                          >
                            <IconTrash size={20} />
                          </ActionIcon>
                        </Tooltip>
                        {canChangeOwner ? (
                          <Tooltip
                            label={Localization.strings.CHANGE_OWNER || 'Change owner'}
                            position="bottom"
                          >
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={()=>this.changeOwnerClicked(row)}
                              radius="xl"
                            >
                              <IconUserCog size={20} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                      </div>
                      </div>
                      <If condition={isAdminUser}>
                        <Then>
                          <div style={{backgroundColor:qaStateColor, padding:'4px 0 0 0'}}></div>
                        </Then>
                      </If>
                    </Card>
                  </div>
                );
              })}
            </SimpleGrid>
          </Else>
        </If>
        <ChangeOwnerDialog
          root={this.props.root}
          opened={!!this.state.changeOwnerLocale}
          locale={this.state.changeOwnerLocale}
          onClose={this.changeOwnerClosed}
          onChanged={this.changeOwnerChanged}
        />
      </div>
    )
  }
}

export default LocaleListView;
